/**
 * Virtueller Joystick: reine Geometrie zwischen Ankerpunkt (wo der Finger
 * aufsetzte) und Zeiger (wo er jetzt ist). Bildschirmachsen, y nach unten.
 */

import { RENN_FAKTOR } from '@etown/shared';

/** Auslenkung in Pixeln, bei der der Vektor den Betrag 1 (Gehen) erreicht. */
export const JOYSTICK_RADIUS_PX = 56;
/** Bei diesem Vielfachen des Radius ist volles Renntempo erreicht — der Daumen schiebt ueber den Ring hinaus. */
export const JOYSTICK_RENN_AB = 1.5;
/** Anteil des Radius, in dem sich nichts tut — ein ruhender Daumen zittert. */
export const JOYSTICK_TOTZONE = 0.15;

export interface Punkt {
  x: number;
  y: number;
}

/** Auslenkung in Bildschirmachsen: Betrag 0..1 im Ring, darueber stetig bis RENN_FAKTOR. */
export function joystickAuslenkung(
  anker: Punkt,
  zeiger: Punkt,
  radiusPx = JOYSTICK_RADIUS_PX,
  totzone = JOYSTICK_TOTZONE,
): Punkt {
  const dx = (zeiger.x - anker.x) / radiusPx;
  const dy = (zeiger.y - anker.y) / radiusPx;
  const laenge = Math.hypot(dx, dy);
  if (laenge <= totzone) return { x: 0, y: 0 };
  // Jenseits der Totzone stetig von 0 auf 1 hochfahren, statt zu springen;
  // ueber den Ring hinaus weiter bis zum Renntempo
  const betrag =
    laenge <= 1
      ? (laenge - totzone) / (1 - totzone)
      : 1 + Math.min(1, (laenge - 1) / (JOYSTICK_RENN_AB - 1)) * (RENN_FAKTOR - 1);
  return { x: (dx / laenge) * betrag, y: (dy / laenge) * betrag };
}

/**
 * Wo der Knopf gezeichnet wird: dem Finger nach, ueber den Ring hinaus bis
 * zur Rennweite — so sieht man, dass man rennt — und dort festgehalten.
 */
export function knopfPosition(anker: Punkt, zeiger: Punkt, radiusPx = JOYSTICK_RADIUS_PX): Punkt {
  const dx = zeiger.x - anker.x;
  const dy = zeiger.y - anker.y;
  const laenge = Math.hypot(dx, dy);
  const weite = radiusPx * JOYSTICK_RENN_AB;
  if (laenge <= weite) return { x: zeiger.x, y: zeiger.y };
  return { x: anker.x + (dx / laenge) * weite, y: anker.y + (dy / laenge) * weite };
}

/** Rennt der Finger — steht der Knopf ausserhalb des Rings? */
export function knopfRennt(basis: Punkt, knopf: Punkt, radiusPx = JOYSTICK_RADIUS_PX): boolean {
  return Math.hypot(knopf.x - basis.x, knopf.y - basis.y) > radiusPx + 1;
}

/** Bildschirm-Auslenkung in blickrelative Bewegung: oben ist vorwaerts. */
export function joystickZuBewegung(auslenkung: Punkt): { vor: number; seit: number } {
  return { vor: -auslenkung.y, seit: auslenkung.x };
}
